/**
 * V2 PaSurveyEndPage
 * Survey completion page with Athena integration
 */

import { memo, useState, useEffect, Component, type ReactNode, type ErrorInfo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { Enum_Survey_Access_Restriction, Enum_Translation_Locale, notEmpty } from '@cyren/common-lib';
import { Button } from '@mui/material';
import { toast } from 'react-toastify';
import { SystemLocaleCodes, useStaticLocales } from '../../../../admin/hooks/locales/use-static-locale';
import Footer from '../../../../app/Footer';
import LoadingAnim from '../../../../components/LoadingAnim';
import LogoPatientDynamic from '../../../../components/LogoPatientDynamic';
import PaTreeHeader from './components/PaTreeHeader';
import SurveyContainer from './SurveyContainer';
import thumbsUp from '../../../../assets/ill/Thumbs_up.svg';
import ClinicalTrialActions from '../../../../patient/survey/clinical-trial/ClinicalTrialActions';
import ClinicalTrialAds from '../../../../patient/survey/clinical-trial/ClinicalTrialAds';
import ClinicalTrialCard from '../../../../patient/survey/clinical-trial/ClinicalTrialCard';
import ClinicalTrialInquiryModal from '../../../../patient/survey/clinical-trial/ClinicalTrialInquiryModal';
import { usePatientTransData } from '../../../hooks/use-patient-trans-data';
import { useSurveyMeta } from '../../../hooks/use-survey-meta';
import { useTranslations } from '../../../../patient/hooks/use-translations';
import type { TOptions } from '../../../../patient/pa-prop-types';
import { usePatientLocale } from '../../../hooks/use-patient-locale';
import { sanitizeSurveyTranslationForLocale } from './lib/sanitize-survey-translation';
import { useOnNavChange } from '../../../../hooks/use-on-nav-change';
import { mapDefaultLanguageToLocaleKey } from './locale';
import { getLocaleCode } from '../../organization/configurations/language/constants';

const collapCls =
  'collapse collapse-arrow border-2 border-black border-opacity-10 rounded-none first:rounded-t-xl last:rounded-b-xl border-t-0 first:border-t-2 font-base';
const collapHeaderCls = 'collapse-title text-base font-medium';
const collapBodyCls = 'collapse-content opacity-70 text-base';
const FAQ_META_CACHE_KEY = 'v2-pa-faq-meta-cache';

function InlineProgressBar({ progress }: { progress: number }) {
  return (
    <div className='w-full h-2 bg-black/10 rounded-full overflow-hidden'>
      <div className='h-full bg-primary' style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }} />
    </div>
  );
}

/**
 * Page Content Component
 */
function PaSurveyEndPageContent() {
  const nav = useNavigate();
  const params = useParams();
  const { localeKey: patientLocaleKey, localeCode: patientLocaleCode, userLocaleKey } = usePatientLocale();
  useOnNavChange({
    block: true,
    onChange: () => {
      toast.info(sltStr({ key: 'msg-cannot-go-back' }), {
        toastId: 'msg-cannot-go-back',
      });
    },
  });

  // Get all data from Container
  const {
    isLoading,
    surveyState,
    reportState,
    survey,
    visit,
    org,
    defaultLanguage,
    navigation,
    athena,
    updateVisit,
  } = SurveyContainer.useContainer();

  const { baseUrl } = navigation;
  const { isProcessing: isAthenaProcessing, handleProviderAndPharmacy } = athena;
  const defaultLocaleKey = mapDefaultLanguageToLocaleKey(defaultLanguage);
  const effectiveLocaleKey = (userLocaleKey || defaultLocaleKey || patientLocaleKey || 'english').toLowerCase();
  const effectiveLocaleCode = (getLocaleCode(effectiveLocaleKey) || patientLocaleCode) as SystemLocaleCodes;
  const { sltStr } = useStaticLocales({ localeCode: effectiveLocaleCode });
  const [, { t: tEffective }] = useTranslations({ locale: effectiveLocaleKey as Enum_Translation_Locale });
  const [, { t: tDefault }] = useTranslations({ locale: defaultLocaleKey as Enum_Translation_Locale });
  const [, { t: tPatient }] = useTranslations({ locale: patientLocaleKey as Enum_Translation_Locale });

  // Local state
  const [selectedFaqIndex, setSelectedFaqIndex] = useState<number | null>(null);
  const [athenaIntegrationExecuted, setAthenaIntegrationExecuted] = useState(false);
  const [isWaitingListOpen, setIsWaitingListOpen] = useState(false);
  const isF1z7sna =
    params.shortId === 'f1z7sna' || params.shortId === 'o76xz7c' || params.shortId === 'u7etsub';

  const { transKeys, metaItems } = useSurveyMeta({
    surveyId: survey?.id ? String(survey.id) : undefined,
    surveyShortId: params.shortId,
    metaKey: 'faq',
  });

  useEffect(() => {
    if (!notEmpty(metaItems)) return;
    try {
      sessionStorage.setItem(FAQ_META_CACHE_KEY, JSON.stringify(metaItems));
    } catch (error) {
      console.warn('Failed to cache faq meta items:', error);
    }
  }, [metaItems]);

  const tOptions = (reportState?.systemValueMap
    ? Object.fromEntries(
        Object.entries(reportState?.systemValueMap).filter(([, value]) => typeof value === 'string'),
      )
    : {}) as TOptions;
  const titleCandidates = [
    { text: tEffective('finish-page-title', tOptions, { fallbackValue: null }), locale: effectiveLocaleKey },
    { text: tPatient('finish-page-title', tOptions, { fallbackValue: null }), locale: patientLocaleKey },
    { text: tDefault('finish-page-title', tOptions, { fallbackValue: null }), locale: defaultLocaleKey },
  ];
  const subtitleCandidates = [
    { text: tEffective('finish-page-desc', tOptions, { fallbackValue: null }), locale: effectiveLocaleKey },
    { text: tPatient('finish-page-desc', tOptions, { fallbackValue: null }), locale: patientLocaleKey },
    { text: tDefault('finish-page-desc', tOptions, { fallbackValue: null }), locale: defaultLocaleKey },
  ];

  const titleLabel =
    titleCandidates
      .map((candidate) =>
        sanitizeSurveyTranslationForLocale(
          candidate.text,
          String(candidate.locale || effectiveLocaleKey).toLowerCase(),
        ),
      )
      .find(Boolean) || null;
  const subtitleLabel =
    subtitleCandidates
      .map((candidate) =>
        sanitizeSurveyTranslationForLocale(
          candidate.text,
          String(candidate.locale || effectiveLocaleKey).toLowerCase(),
        ),
      )
      .find(Boolean) || null;

  const pageKeys = survey?.metadata?.finishedPageKeys || [];
  const [{ loadingTrans: loadingTransEffective }] = usePatientTransData({
    keys: pageKeys,
    locale: effectiveLocaleKey as Enum_Translation_Locale,
  });
  const [{ loadingTrans: loadingTransDefault }] = usePatientTransData({
    keys: pageKeys,
    locale: defaultLocaleKey as Enum_Translation_Locale,
  });
  const [{ loadingTrans: loadingTransPatient }] = usePatientTransData({
    keys: pageKeys,
    locale: patientLocaleKey as Enum_Translation_Locale,
  });
  usePatientTransData({
    keys: transKeys,
    locale: effectiveLocaleKey as Enum_Translation_Locale,
  });

  useEffect(() => {
    try {
      localStorage.removeItem('encounterCheck');
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.includes('AdminPatientSurveyState') || key.includes('PaReportState'))
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.warn('localStorage cleanup failed:', error);
    }
  }, []);

  // Execute Athena integration when patient details are available
  useEffect(() => {
    if (surveyState.patientDetails && !athenaIntegrationExecuted) {
      const executeAthenaIntegration = async () => {
        try {
          // Clean up localStorage encounter check
          localStorage.removeItem('encounterCheck');

          // Handle provider and pharmacy updates
          await handleProviderAndPharmacy();

          // Navigate to terminated page
          nav(`${baseUrl}/terminated`);
        } catch (error) {
          console.error('Athena integration error:', error);
          nav(`${baseUrl}/terminated`);
        } finally {
          setAthenaIntegrationExecuted(true);
        }
      };

      executeAthenaIntegration();
    }
  }, [surveyState.patientDetails, athenaIntegrationExecuted, handleProviderAndPharmacy, nav, baseUrl]);

  // Safety timeout: skip translation loading after 5s to prevent permanent blank page
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setLoadingTimedOut(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const transStillLoading = !loadingTimedOut && (loadingTransEffective || loadingTransDefault || loadingTransPatient);

  // Loading state
  if (isLoading || isAthenaProcessing || transStillLoading) {
    return (
      <div className='flex flex-1 w-full'>
        <LoadingAnim className='h-full p-10 w-full' />
      </div>
    );
  }

  // Check if provider report should be accessible
  const canViewProviderReport =
    survey?.access_restriction &&
    survey.access_restriction !== Enum_Survey_Access_Restriction.ProviderOnly;

  return (
    <div className='flex-1 flex-col gap-5 justify-between'>
      <div className='flex-1 flex-col gap-5'>
        <PaTreeHeader showFullBar noBack logoUrl={org?.logoUrl} />

        <div className='px-5'>
          <div className='flex-center'>
            <LogoPatientDynamic logoUrl={org?.logoUrl} />
          </div>

          {/* Thank You Message - only shown when not archived */}
          {!visit?.archived && (
            <div className='flex-center flex-col gap-4'>
              <div className='pt-12 pb-6 w-full flex justify-center'>
                <img src={thumbsUp} alt='Thumbs Up' className='block mx-auto max-w-[180px] w-full h-auto' />
              </div>

              <div className='flex-col gap-2 flex-center-y'>
                <h4 className='font-bold sm'>{titleLabel || 'Thank You!'}</h4>
                <div className='text-base opacity-70 px-2 text-center'>
                  {subtitleLabel || 'Your information has been sent to your care team because your visit has started.'}
                </div>
              </div>
            </div>
          )}

          {!visit?.archived && (
            <div className='mt-6 space-y-4'>
              <div className='border-t border-border' />

              {notEmpty(metaItems) && (
                <div className='flex-col py-4 w-full'>
                  {metaItems.map((item, idx) => {
                    const open = selectedFaqIndex === idx;
                    return (
                      <div
                        key={item.id}
                        className={classNames(collapCls, open && 'collapse-open')}
                        onClick={() => setSelectedFaqIndex(open ? null : idx)}
                      >
                        <div className={collapHeaderCls}>
                          {tEffective(item.qnaQuestionKey, tOptions) ?? ''}
                        </div>
                        <div className={collapBodyCls}>
                          <p>{tEffective(item.qnaAnswerKey, tOptions) ?? ''}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {isF1z7sna && (
                <div className='bg-primary bg-opacity-5 rounded-2xl p-5 shadow-soft space-y-4'>
                  <ClinicalTrialCard />
                  <ClinicalTrialAds />
                  <ClinicalTrialActions onWaitingList={() => setIsWaitingListOpen(true)} />
                </div>
              )}
            </div>
          )}

          {/* Archived Message */}
          {visit?.archived && (
            <div className='flex-center flex-col gap-4 py-10'>
              <div className='flex-col gap-2 flex-center-y'>
                <div className='text-base opacity-70 px-2 text-center flex-1 bg-primary bg-opacity-10 rounded-xl w-full p-2'>
                  {sltStr({ key: 'finished-archived' })}
                </div>
              </div>
            </div>
          )}

          {/* View Provider Report Button */}
          {!isF1z7sna && canViewProviderReport && (
            <div className='flex-center flex-col gap-4 py-10'>
              <div className='flex-col gap-2 flex-center-y'>
                <Button
                  variant='contained'
                  onClick={() => nav(`/sp/${visit?.uuid}`)}
                >
                  View Medical Provider Report
                </Button>
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {!isF1z7sna && notEmpty(metaItems) && (
            <div className='flex-col py-4 w-full'>
              {metaItems.map((item, idx) => {
                const open = selectedFaqIndex === idx;
                return (
                  <div
                    key={item.id}
                    className={classNames(collapCls, open && 'collapse-open')}
                    onClick={() => setSelectedFaqIndex(open ? null : idx)}
                  >
                    <div className={collapHeaderCls}>
                      {tEffective(item.qnaQuestionKey, tOptions) ?? ''}
                    </div>
                    <div className={collapBodyCls}>
                      <p>{tEffective(item.qnaAnswerKey, tOptions) ?? ''}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className='hidden flex-none'>
        <Footer />
      </div>
      {isF1z7sna && (
        <ClinicalTrialInquiryModal
          open={isWaitingListOpen}
          onOpenChange={setIsWaitingListOpen}
          trialName='맞춤 임상시험'
        />
      )}
    </div>
  );
}

/**
 * Error boundary for end page - prevents blank screen on crash
 */
class EndPageErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('PaSurveyEndPage error:', error, info);
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      return (
        <div className='flex-1 flex-col gap-5 items-center justify-center py-20 px-5'>
          <img src={thumbsUp} alt='Thumbs Up' className='block mx-auto max-w-[180px] w-full h-auto' />
          <h4 className='font-bold sm text-center'>Thank You!</h4>
          <div className='text-base opacity-70 text-center'>
            Your information has been sent to your care team.
          </div>
        </div>
      );
    }
    return children;
  }
}

/**
 * Main Page Component
 */
function PaSurveyEndPage() {
  return (
    <EndPageErrorBoundary>
      <SurveyContainer.Provider>
        <PaSurveyEndPageContent />
      </SurveyContainer.Provider>
    </EndPageErrorBoundary>
  );
}

export default memo(PaSurveyEndPage);
